import React from 'react';
import * as S from './GenerateModal.style';

const GenerateModal = ({
  selectedFile,
  selectedReports,
  selectAllReports,
  reportFileName,
  availableFiles,
  availableReports,
  // 🆕 과년도 데이터 관련 props 추가
  baseYear,
  yearlyFiles,
  selectedMembersFile,
  // 핸들러 props
  onFileSelect,
  onReportToggle,
  onSelectAllReportsToggle,
  onReportFileNameChange,
  // 🆕 과년도 데이터 관련 핸들러 추가
  onBaseYearChange,
  onYearlyFileSelect,
  onGenerate,
  disabled = false,
  onHosMemberFileSelect
}) => {
  const handleFileSelect = (event) => {
    if (disabled) return;
    onFileSelect?.(event);
  };

  const handleReportToggle = (reportId) => {
    if (disabled) return;
    onReportToggle?.(reportId);
  };

  const handleSelectAllReportsToggle = () => {
    if (disabled) return;
    onSelectAllReportsToggle?.();
  };

  const handleReportFileNameChange = (event) => {
    if (disabled) return;
    onReportFileNameChange?.(event);
  };

  // 🆕 기준 연도 변경 핸들러
  const handleBaseYearChange = (event) => {
    if (disabled) return;
    onBaseYearChange?.(event);
  };

  // 🆕 과년도 파일 선택 핸들러
  const handleYearlyFileSelect = (year, event) => {
    if (disabled) return;
    onYearlyFileSelect?.(year, event);
  };

  const handleHosMemberFileSelect = (event) => {
    if (disabled) return;
    onHosMemberFileSelect?.(event);
  };

  const handleGenerate = () => {
    if (disabled) return;
    onGenerate?.();
  };

  // 🆕 과년도 년도 목록 생성 (기준년도 기준 1-4년 전)
  const getYearlyOptions = () => {
    if (!baseYear) return [];
    const currentYear = parseInt(baseYear);
    return [
      { year: currentYear - 1, label: `${currentYear - 1} 데이터` },
      { year: currentYear - 2, label: `${currentYear - 2} 데이터` },
      { year: currentYear - 3, label: `${currentYear - 3} 데이터` },
      { year: currentYear - 4, label: `${currentYear - 4} 데이터` }
    ];
  };

  return (
    <S.ModalContainer disabled={disabled}>

      {disabled && (
        <S.GeneratingOverlay>
          <S.GeneratingContent>
            <S.GeneratingSpinner />
            <S.GeneratingText>보고서를 생성하고 있습니다...</S.GeneratingText>
            <S.GeneratingSubtext>
              이 작업은 몇 분 정도 소요될 수 있습니다.
            </S.GeneratingSubtext>
          </S.GeneratingContent>
        </S.GeneratingOverlay>
      )}


      {/* 🆕 기준 연도 입력 섹션 */}
      <S.SectionTitle>기준 연도 설정</S.SectionTitle>
      
      <S.FileSelectGroup>
        <S.FileSelectLabel>보고서 기준 연도</S.FileSelectLabel>
        <S.FileSelectWrapper>
          <S.YearInput 
            type="number"
            value={baseYear}
            onChange={handleBaseYearChange}
            placeholder="예: 2025"
            min="2000"
            max="2100"
            disabled={disabled}
          />
        </S.FileSelectWrapper>
      </S.FileSelectGroup>

      <S.SectionTitle>분석데이터 선택하기</S.SectionTitle>
      
      <S.FileSelectGroup>
        <S.FileSelectLabel>기준 연도 데이터</S.FileSelectLabel>
        <S.FileSelectWrapper>
          <S.FileSelect 
            value={selectedFile} 
            onChange={handleFileSelect}
            hasValue={!!selectedFile}
            disabled={disabled}
          >
            <option value="">원본 데이터 파일을 선택하세요</option>
            {availableFiles.map(file => (
              <option key={file.id} value={file.id}>
                {file.name}
              </option>
            ))}
          </S.FileSelect>
        </S.FileSelectWrapper>
      </S.FileSelectGroup>

        <S.SectionTitle>과년도 데이터 선택하기</S.SectionTitle>

      {/* 🆕 과년도 데이터 선택 섹션 */}
      {baseYear && getYearlyOptions().map(({ year, label }) => (
        <S.FileSelectGroup key={year}>
          <S.FileSelectLabel>{label}</S.FileSelectLabel>
          <S.FileSelectWrapper>
            <S.FileSelect 
              value={yearlyFiles[year] || ''} 
              onChange={(event) => handleYearlyFileSelect(year, event)}
              hasValue={!!yearlyFiles[year]}
              disabled={disabled}
            >
              <option value="">선택 안함 (선택사항)</option>
              {availableFiles.map(file => (
                <option key={file.id} value={file.id}>
                  {file.name}
                </option>
              ))}
            </S.FileSelect>
          </S.FileSelectWrapper>
        </S.FileSelectGroup>
      ))}
      
      <S.SectionTitle>연계 데이터 선택하기</S.SectionTitle>

      <S.FileSelectGroup>
        <S.FileSelectLabel>병원 목록 파일</S.FileSelectLabel>
        <S.FileSelectWrapper>
          <S.FileSelect 
            value={selectedMembersFile} 
            onChange={handleHosMemberFileSelect}
            hasValue={!!selectedMembersFile}
            disabled={disabled}
          >
            <option value="">병원 목록 파일을 선택하세요</option>
            {availableFiles.map(file => (
              <option key={file.id} value={file.id}>
                {file.name}
              </option>
            ))}
          </S.FileSelect>
        </S.FileSelectWrapper>
      </S.FileSelectGroup>

      <S.ReportSelectGroup>
        <S.ReportSelectTitle>분석결과표 선택</S.ReportSelectTitle>
        
        <S.SelectAllWrapper>
          <S.Checkbox 
            type="checkbox"
            checked={selectAllReports}
            onChange={handleSelectAllReportsToggle}
            disabled={disabled}
          />
          <S.CheckboxLabel disabled={disabled}>전체 선택</S.CheckboxLabel>
        </S.SelectAllWrapper>

        <S.ReportListBox disabled={disabled}>
          {availableReports.map(report => (
            <S.ReportItem key={report.id}>
              <S.Checkbox 
                type="checkbox"
                checked={selectedReports.some(selected => selected.id === report.id)}
                onChange={() => handleReportToggle(report.id)}
                disabled={disabled}
              />
              <S.ReportLabel disabled={disabled}>{report.title}</S.ReportLabel>
            </S.ReportItem>
          ))}
        </S.ReportListBox>
      </S.ReportSelectGroup>

      <S.FileNameGroup>
        <S.FileNameLabel>보고서 파일 이름</S.FileNameLabel>
        <S.FileNameInput 
          type="text"
          value={reportFileName}
          onChange={handleReportFileNameChange}
          placeholder="report"
          disabled={disabled}
        />
      </S.FileNameGroup>

      <S.GenerateButtonWrapper>
        <S.GenerateButton 
          onClick={handleGenerate}
          disabled={disabled || !selectedFile || selectedReports.length === 0}
        >
          {disabled ? '생성 중...' : '생성하기'}
        </S.GenerateButton>
      </S.GenerateButtonWrapper>
    </S.ModalContainer>
  );
};

export default GenerateModal;